import { i18next } from '@frontend/i18n';
import { SchemaCallIntelV2Service, SchemaTaskService } from './service';
import {
  AppointmentTypeEnum,
  InfoTipId,
  ServiceQualityFlagEnum,
  TimePeriodTypeEnum,
  OfficeHoursEnum,
  CallStatusEnum,
  ContactTypeEnum,
} from './types';

export const STANDARD_QUERY_ENDPOINT_NAMES = [
  'GetCallsOverview',
  'GetCallEditHistory',
  'OverviewStats',
  'DemoServiceQuality',
  'RecentCalls',
  'GetCallMetadataNew',
] as const satisfies (keyof typeof SchemaCallIntelV2Service)[];

export const STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES = [
  'ListTasks',
] as const satisfies (keyof typeof SchemaTaskService)[];

export const INFINITE_QUERY_ENDPOINT_NAMES = ['ListCalls'] as const satisfies (keyof Omit<
  typeof SchemaCallIntelV2Service,
  (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number]
>)[];

export const QUERY_ENDPOINT_NAMES = [...STANDARD_QUERY_ENDPOINT_NAMES, ...INFINITE_QUERY_ENDPOINT_NAMES] as const;

export const TASK_QUERY_ENDPOINT_NAMES = [...STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES] as const;

export const MUTATION_ENDPOINT_NAMES = [] as const satisfies (keyof Omit<
  typeof SchemaCallIntelV2Service,
  (typeof QUERY_ENDPOINT_NAMES)[number]
>)[];

export const timePeriodLabels: Record<string, string> = {
  'last-14-days': i18next.t('Last 14 Days', { ns: 'analytics' }),
  'last-30-days': i18next.t('Last 30 Days', { ns: 'analytics' }),
  'last-7-days': i18next.t('Last 7 Days', { ns: 'analytics' }),
  'last-month': i18next.t('Last Month', { ns: 'analytics' }),
  'last-quarter': i18next.t('Last Quarter', { ns: 'analytics' }),
  'last-week': i18next.t('Last Week', { ns: 'analytics' }),
  'this-month': i18next.t('This Month', { ns: 'analytics' }),
  'this-quarter': i18next.t('This Quarter', { ns: 'analytics' }),
  'this-week': i18next.t('This Week', { ns: 'analytics' }),
  today: i18next.t('Today', { ns: 'analytics' }),
  yesterday: i18next.t('Yesterday', { ns: 'analytics' }),
};

export const useTimePeriodFilterLabels = () => {
  return timePeriodLabels;
};

export const baseUrlV2 = 'call-intelligence/v2';

export const ServiceQualityFlagPriorityMap = {
  [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: 1,
  [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: 2,
  [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: 3,
};

export const AllowedServiceIssuesRouteLabelMap = {
  'treatment-concerns': ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT,
  'unresolved-contact-attempts': ServiceQualityFlagEnum.FLAG_REPEAT_CALLER,
  'manager-escalations': ServiceQualityFlagEnum.FLAG_CALL_ESCALATION,
};

export const ServiceQualityTooltipIdMap: Partial<Record<ServiceQualityFlagEnum, InfoTipId>> = {
  [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: 'callEscalationServiceIssue',
  [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: 'ineffectiveTreatmentServiceIssue',
  [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: 'repeatCallerServiceIssue',
};

export const CallIntelTimePeriodLabelToType: Record<string, TimePeriodTypeEnum> = {
  Today: TimePeriodTypeEnum.TIMEPERIOD_TYPE_THIS_DAY,
  'This Week': TimePeriodTypeEnum.TIMEPERIOD_TYPE_THIS_WEEK,
  'This Month': TimePeriodTypeEnum.TIMEPERIOD_TYPE_THIS_MONTH,
  // If in future we support these:
  'This Quarter': TimePeriodTypeEnum.TIMEPERIOD_TYPE_THIS_QUARTER,
  'This Year': TimePeriodTypeEnum.TIMEPERIOD_TYPE_THIS_YEAR,
};

export const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/* Demo user email domain constants
 */
export const DEMO_DOMAINS = {
  PATTERSON_DENTAL: '@pattersondental.com',
  PATTERSON_COMPANIES: '@pattersoncompanies.com',
  DEMO_WEAVE: '@demoweave.com',
  WEAVE_USERS: '@weave-users.com', //For new demo accounts to be used by sales
} as const;

export const ALL_OFFICE_HOURS_ID = 'All';

export const ALL_OFFICE_HOURS_VALUES = [OfficeHoursEnum.OFFICE_HOURS_OPEN, OfficeHoursEnum.OFFICE_HOURS_CLOSED];
/**
 * Call Intel only operates on these call statuses.
 */
export const CALL_INTEL_CALL_STATUSES = [
  CallStatusEnum.CALL_STATUS_PENDING,
  CallStatusEnum.CALL_STATUS_TRANSCRIBING,
  CallStatusEnum.CALL_STATUS_INFERENCING,
  CallStatusEnum.CALL_STATUS_COMPLETE,
  CallStatusEnum.CALL_STATUS_FAILED,
  CallStatusEnum.CALL_STATUS_ERROR,
  CallStatusEnum.CALL_STATUS_SKIPPED,
  CallStatusEnum.CALL_STATUS_VOICEMAIL,
  CallStatusEnum.CALL_STATUS_DEFERRED,
] as const;

export const APPOINTMENT_TYPE_DENTAL = [
  AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN,
  AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING,
  AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC,
  AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY,
  AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER,
] as const;

export const APPOINTMENT_TYPE_OPTO = [
  AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN,
  AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY,
  AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION,
  AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES,
  AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER,
] as const;

export const APPOINTMENT_TYPE_VET = [
  AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET,
  AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY,
  AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS,
  AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER,
] as const;

export const NON_PATIENT_CONTACT_TYPES = [ContactTypeEnum.CONTACT_NOT_A_PATIENT, ContactTypeEnum.CONTACT_UNKNOWN];
